/**
 * Breadcrumb Navigation Component
 * Shows the current step in the survey data workflow.
 * Once a step is unlocked (visited), it remains clickable for the session and when that project is opened again.
 */
import React, { useMemo, useEffect } from 'react';
import { useNavigate, useLocation, useParams } from 'react-router-dom';
import { Box, Breadcrumbs, Typography, Link as MuiLink } from '@mui/material';
import { useProjectProgress } from '../../contexts/ProjectProgressContext';
import {
  NavigateNext as NavigateNextIcon,
  CloudUpload as UploadIcon,
  Assessment as DataIcon,
  FilterList as FilterIcon,
  Dashboard as DashboardIcon,
  ViewModule as CardsIcon,
} from '@mui/icons-material';
import branding from '../../styles/branding';
import './Breadcrumb.css';

const STORAGE_KEY_PREFIX = 'breadcrumb_max_step_';

const Breadcrumb = ({ currentStep }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { jobId, projectId } = useParams();
  const projectProgress = useProjectProgress();
  const { checkNavigation, currentProject } = projectProgress;
  const isEditingDashboard = Boolean(projectProgress.isEditingDashboard);
  const identifier = jobId || projectId || currentProject?.projectId;
  
  // Sanitize identifier to only allow alphanumeric characters and hyphens to prevent Open Redirect
  const safeIdentifier = identifier && /^[a-zA-Z0-9-]+$/.test(identifier) ? identifier : null;

  // Define the breadcrumb steps (use identifier for projectId or jobId)
  const steps = useMemo(
    () => [
      {
        label: 'Upload',
        path: '/landing',
        icon: <UploadIcon />,
        step: 'upload',
        description: 'Upload your survey data',
      },
      {
        label: 'Data',
        path: safeIdentifier ? `/preview/${safeIdentifier}` : null,
        icon: <DataIcon />,
        step: 'preview',
        description: 'Review your data',
        requiresJobId: true,
      },
      {
        label: 'Filters',
        path: safeIdentifier ? `/filters/${safeIdentifier}` : null,
        icon: <FilterIcon />,
        step: 'filters',
        description: 'Configure filters',
        requiresJobId: true,
      },
      {
        label: 'Cards',
        path: safeIdentifier ? `/cards/${safeIdentifier}` : null,
        icon: <CardsIcon />,
        step: 'cards',
        description: 'Create visualization cards',
        requiresJobId: true,
      },
      {
        label: 'Dashboard',
        path: safeIdentifier ? `/dashboard/${safeIdentifier}` : '/dashboards',
        icon: <DashboardIcon />,
        step: 'dashboard',
        description: 'View dashboards',
        requiresJobId: false,
      },
    ],
    [safeIdentifier, identifier]
  );

  // Determine the current step index
  const currentStepIndex = useMemo(() => {
    if (currentStep) {
      return steps.findIndex((s) => s.step === currentStep);
    }

    // Determine from location
    const path = location.pathname;
    if (path.includes('/landing') || path === '/') return 0;
    if (path.includes('/preview')) return 1;
    if (path.includes('/filters')) return 2;
    if (path.includes('/cards')) return 3;
    if (path.includes('/dashboard')) return 4;

    return 0;
  }, [currentStep, location.pathname, steps]);

  // Max step index unlocked for this project/job. Derived from storage + current step every render so it's never stale.
  const safeCurrentStepIndex = Math.max(0, currentStepIndex);
  const maxUnlockedStepIndex = useMemo(() => {
    if (!identifier || identifier === '') return safeCurrentStepIndex;
    try {
      const stored = window.localStorage.getItem(STORAGE_KEY_PREFIX + identifier);
      const parsed = stored != null ? parseInt(stored, 10) : 0;
      const fromStorage = isNaN(parsed) ? 0 : parsed;
      return Math.max(fromStorage, safeCurrentStepIndex);
    } catch {
      return safeCurrentStepIndex;
    }
  }, [identifier, safeCurrentStepIndex]);

  // Persist max unlocked step when we're on a page with an identifier so it survives navigation and re-open.
  useEffect(() => {
    if (!identifier || identifier === '') return;
    try {
      const stored = window.localStorage.getItem(STORAGE_KEY_PREFIX + identifier);
      const parsed = stored != null ? parseInt(stored, 10) : 0;
      const fromStorage = isNaN(parsed) ? 0 : parsed;
      const nextMax = Math.max(fromStorage, safeCurrentStepIndex);
      window.localStorage.setItem(STORAGE_KEY_PREFIX + identifier, String(nextMax));
    } catch {
      // ignore
    }
  }, [identifier, safeCurrentStepIndex]);

  // A step is clickable if it is not disabled and has been unlocked (index <= maxUnlockedStepIndex)
  const isStepUnlocked = (step, index) => {
    if (step?.step === 'dashboard' && isEditingDashboard) return true;
    return index <= maxUnlockedStepIndex;
  };

  // Handle breadcrumb click: check unsaved changes before navigating to another step
  const handleBreadcrumbClick = (step, index) => {
    if (!step.path || (step.requiresJobId && !identifier)) return;
    if (!isStepUnlocked(step, index)) return;
    if (checkNavigation(step.path) === false) return; // Save dialog shown; don't navigate
    
    // Validate path to prevent Open Redirect
    let safePath = step.path;
    if (safePath.startsWith('//') || !safePath.startsWith('/')) {
      console.warn('Invalid redirect path detected:', safePath);
      return;
    }
    
    navigate(safePath);
  };

  return (
    <Box
      sx={{
        bgcolor: 'rgba(255, 255, 255, 0.95)',
        py: 1,
        px: { xs: 2, sm: 2.5, md: 3 },
        borderBottom: '1px solid rgba(0, 0, 0, 0.1)',
        mb: 0,
      }}
    >
      <Breadcrumbs
        separator={<NavigateNextIcon fontSize="small" />}
        aria-label="breadcrumb"
      >
        {steps.map((step, index) => {
          const isActive = index === currentStepIndex;
          const isDisabled = !step.path || (step.requiresJobId && !identifier);
          const isClickable = !isDisabled && isStepUnlocked(step, index);
          // Accessible (unlocked) steps: black; other steps: grey. All steps always visible.
          const textColor = isClickable ? 'rgba(0, 0, 0, 0.87)' : '#9e9e9e';
          const iconColor = isClickable ? 'rgba(0, 0, 0, 0.87)' : '#9e9e9e';

          return (
            <Box
              key={step.step}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                cursor: isClickable ? 'pointer' : 'default',
                opacity: 1,
              }}
              onClick={() => isClickable && handleBreadcrumbClick(step, index)}
            >
              <Box
                sx={{
                  color: iconColor,
                  display: 'flex',
                  alignItems: 'center',
                }}
              >
                {step.icon}
              </Box>
              {isClickable ? (
                <MuiLink
                  component="button"
                  variant="body1"
                  underline="hover"
                  sx={{
                    fontFamily: branding.fonts.primary,
                    fontWeight: isActive ? 600 : 400,
                    color: textColor,
                  }}
                >
                  {step.label}
                </MuiLink>
              ) : (
                <Typography
                  variant="body1"
                  sx={{
                    fontFamily: branding.fonts.primary,
                    fontWeight: isActive ? 600 : 400,
                    color: textColor,
                  }}
                >
                  {step.label}
                </Typography>
              )}
            </Box>
          );
        })}
      </Breadcrumbs>
    </Box>
  );
};

export default Breadcrumb;





